import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { CaretDown, CaretRight, Barbell, Moon, HeartStraight, Scales, CheckCircle, X, MinusCircle } from "@phosphor-icons/react";

/**
 * ActivityLog — unified per-day timeline for a client. Used two ways:
 *   1. Client viewing their own history (scope="mine")
 *   2. Coach viewing a specific client (scope="client", passes clientId)
 * Renders newest-first, each day collapsed by default. Expanding a day
 * reveals the per-habit tick breakdown + sleep + wellness + workouts + weight.
 */
const RANGE_OPTIONS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 60, label: "60 days" },
  { days: 90, label: "90 days" },
];

const DAY_LABEL = (iso) => {
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / (1000 * 60 * 60 * 24));
  const dayName = d.toLocaleDateString(undefined, { weekday: "short" });
  const dateBit = d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  if (diff === 0) return `Today · ${dayName} ${dateBit}`;
  if (diff === 1) return `Yesterday · ${dayName} ${dateBit}`;
  return `${dayName} ${dateBit}`;
};

export default function ActivityLog({ scope = "mine", clientId }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [openDate, setOpenDate] = useState(null);

  useEffect(() => {
    (async () => {
      setErr("");
      try {
        const url = scope === "client"
          ? `/coach/clients/${clientId}/activity-log?days=${days}`
          : `/client/activity-log?days=${days}`;
        const { data } = await http.get(url);
        setData(data);
      } catch (e) { setErr(apiError(e)); }
    })();
  }, [scope, clientId, days]);

  if (err) return <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333]" data-testid="activity-log-error">{err}</div>;
  if (!data) return null;

  const labels = data.habit_labels || {};
  const sleepMin = data.sleep_minimum_hours || 6;

  return (
    <div className="id-card" data-testid="activity-log">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="id-label">Activity log</div>
        <div className="flex gap-1" data-testid="activity-log-range">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={
                "text-[10px] font-manrope uppercase tracking-[0.15em] px-2 py-1 border transition-colors " +
                (days === r.days ? "border-[#FF3333] text-[#FF3333] bg-[#FF3333]/5" : "border-[#262626] text-zinc-500 hover:text-white")
              }
              data-testid={`activity-log-range-${r.days}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <ul className="divide-y divide-[#262626]">
        {data.days.map((d) => {
          const habitPct = d.habits.total ? d.habits.done / d.habits.total : 0;
          const habitColor = habitPct === 1 ? "text-[#00CC66]" : habitPct >= 0.5 ? "text-[#FF3333]" : habitPct > 0 ? "text-orange-400" : "text-zinc-600";
          const isOpen = openDate === d.date;
          const isEmpty = d.habits.done === 0 && !d.sleep_hours && !d.wellness && d.workouts.length === 0 && d.weight_kg == null;
          return (
            <li key={d.date} className="py-3" data-testid={`activity-day-${d.date}`}>
              <button
                onClick={() => setOpenDate(isOpen ? null : d.date)}
                className="w-full flex items-center justify-between gap-3 text-left"
                data-testid={`activity-day-toggle-${d.date}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isOpen ? <CaretDown size={12} weight="bold" className="text-zinc-500 shrink-0" /> : <CaretRight size={12} weight="bold" className="text-zinc-500 shrink-0" />}
                  <div className="text-sm text-white font-manrope truncate">{DAY_LABEL(d.date)}</div>
                </div>
                <div className="flex items-center gap-2 text-[11px] font-manrope shrink-0">
                  <span className={habitColor} data-testid={`activity-habits-${d.date}`}>{d.habits.done}/{d.habits.total}</span>
                  {d.sleep_hours != null && (
                    <span className={"flex items-center gap-0.5 " + (d.sleep_hours < sleepMin ? "text-[#FF3333]" : "text-indigo-300")} data-testid={`activity-sleep-${d.date}`}>
                      <Moon size={10} weight="fill" /> {d.sleep_hours}h
                    </span>
                  )}
                  {d.workouts.length > 0 && (
                    <span className="flex items-center gap-0.5 text-[#FF3333]" data-testid={`activity-workouts-${d.date}`}>
                      <Barbell size={10} weight="fill" /> {d.workouts.length}
                    </span>
                  )}
                  {d.weight_kg != null && (
                    <span className="flex items-center gap-0.5 text-zinc-400" data-testid={`activity-weight-${d.date}`}>
                      <Scales size={10} weight="fill" /> {d.weight_kg}kg
                    </span>
                  )}
                  {d.wellness && (
                    <span className="flex items-center gap-0.5 text-pink-400" data-testid={`activity-wellness-${d.date}`}>
                      <HeartStraight size={10} weight="fill" />
                    </span>
                  )}
                  {isEmpty && <span className="text-zinc-700 text-[10px] uppercase tracking-[0.15em]">— quiet —</span>}
                </div>
              </button>

              {isOpen && (
                <div className="mt-3 pl-5 space-y-3" data-testid={`activity-day-details-${d.date}`}>
                  {/* Habit tick breakdown */}
                  {d.habits.total > 0 && (
                    <div>
                      <div className="id-label text-zinc-500 mb-1.5">Habits</div>
                      <ul className="space-y-1">
                        {Object.entries(d.habits.completed).map(([hid, done]) => (
                          <li key={hid} className="flex items-center gap-2 text-xs font-manrope">
                            {done ? (
                              <CheckCircle size={12} weight="fill" className="text-[#00CC66]" />
                            ) : (
                              <MinusCircle size={12} weight="fill" className="text-zinc-700" />
                            )}
                            <span className={done ? "text-white" : "text-zinc-500 line-through"}>{labels[hid] || hid}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Sleep + wellness inline row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {d.sleep_hours != null && (
                      <div className="border border-[#262626] px-3 py-2 flex items-center gap-2">
                        <Moon size={14} weight="fill" className={d.sleep_hours < sleepMin ? "text-[#FF3333]" : "text-indigo-400"} />
                        <div className="text-xs font-manrope">
                          <span className="text-white font-bold">{d.sleep_hours}h sleep</span>
                          {d.sleep_hours < sleepMin && <span className="text-[#FF3333] ml-1">(short)</span>}
                        </div>
                      </div>
                    )}
                    {d.wellness && (
                      <div className="border border-[#262626] px-3 py-2 flex items-center gap-2">
                        <HeartStraight size={14} weight="fill" className="text-pink-400" />
                        <div className="text-xs font-manrope text-zinc-300 space-x-2">
                          {d.wellness.energy != null && <span>Energy {d.wellness.energy}</span>}
                          {d.wellness.stress_levels != null && <span>Stress {d.wellness.stress_levels}</span>}
                          {d.wellness.mood && <span className="text-white">&ldquo;{d.wellness.mood}&rdquo;</span>}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Workouts */}
                  {d.workouts.length > 0 && (
                    <div>
                      <div className="id-label text-zinc-500 mb-1.5">Workouts</div>
                      <ul className="space-y-1">
                        {d.workouts.map((w) => (
                          <li key={w.id} className="flex items-center gap-2 text-xs font-manrope text-zinc-300">
                            <Barbell size={12} weight="bold" className="text-[#FF3333]" />
                            <span className="text-white">{w.day_name}</span>
                            {w.week_number ? <span className="text-zinc-500">· Wk {w.week_number}</span> : null}
                            <span className="text-zinc-500">· {w.exercises_count} exercise{w.exercises_count === 1 ? "" : "s"}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Weight */}
                  {d.weight_kg != null && (
                    <div className="border border-[#262626] px-3 py-2 flex items-center gap-2">
                      <Scales size={14} weight="fill" className="text-zinc-400" />
                      <div className="text-xs font-manrope text-white">
                        {d.weight_kg}kg
                      </div>
                    </div>
                  )}
                  {isEmpty && (
                    <div className="flex items-center gap-2 text-xs text-zinc-500 italic">
                      <X size={12} weight="bold" /> No activity recorded on this day.
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
